import React from "react";
import { useTranslate } from "@tolgee/react";
import * as Yup from "yup";
import {
  CustomFieldDataType,
  CustomFieldObjectType,
} from "../../../clients/graphqlTypes.js";
import { uniqueId } from "lodash-es";
import { CustomFieldConfigFragment } from "./customFieldConfigs.generated.js";
import { Form, Formik } from "formik";
import { SelectField } from "../../commons/form-fields/SelectField.js";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";
import { useCustomFieldObjectTypes } from "./useCustomFieldObjectTypes.js";
import { useCustomFieldDataTypes } from "./useCustomFieldDataTypes.js";
import { Stack } from "@mui/material";
import { Modal } from "@msys/ui";
import { TextField } from "../../commons/form-fields/TextField.js";
import { OptionsField } from "../../commons/form-fields/OptionsField.js";

export interface FormValues {
  objectType: CustomFieldObjectType;
  key: string;
  label: string;
  dataType: CustomFieldDataType;
  group: string;
  allowedValues: string[];
  showAsColumnInList: boolean;
  showInCreateForm: boolean;
  showValueAsChip: boolean;
  isUnique: boolean;
  mandatory: boolean;
}

interface Props {
  handleClose: () => void;
  handleComplete: (
    values: FormValues,
    handleClose: () => void
  ) => Promise<void> | void;
  config: CustomFieldConfigFragment | null;
}

export const AddOrEditCustomFieldModal = ({
  handleClose,
  handleComplete,
  config,
}: Props) => {
  const { t } = useTranslate(["CustomFields", "Global"]);

  const { customFieldObjectTypes, customFieldObjectTypeLabels } =
    useCustomFieldObjectTypes();
  const { customFieldDataTypes, customFieldDataTypeLabels } =
    useCustomFieldDataTypes();

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        objectType: Yup.string()
          .label(t("Page", { ns: "CustomFields" }))
          .required(),
        key: Yup.string()
          .label(t("Field key", { ns: "CustomFields" }))
          .required(),
        label: Yup.string()
          .label(t("Field label", { ns: "CustomFields" }))
          .required(),
        dataType: Yup.string()
          .label(t("Field type", { ns: "CustomFields" }))
          .required(),
        group: Yup.string().label(t("Category", { ns: "CustomFields" })),
      }),
    [t]
  );
  const initialValues = React.useMemo(
    (): FormValues =>
      config
        ? {
            objectType: config.objectType,
            key: config.key,
            label: config.label,
            dataType: config.dataType,
            group: config.group,
            allowedValues: config.allowedValues.split("\n").filter(Boolean),
            showAsColumnInList: config.showAsColumnInList,
            showInCreateForm: config.showInCreateForm,
            showValueAsChip: config.showValueAsChip,
            isUnique: config.isUnique,
            mandatory: config.mandatory,
          }
        : {
            objectType: "Project",
            key: "",
            label: "",
            dataType: "t_text",
            group: "",
            allowedValues: [],
            showAsColumnInList: false,
            showInCreateForm: false,
            showValueAsChip: false,
            isUnique: false,
            mandatory: false,
          },
    [config]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async values => {
        await handleComplete?.(values, handleClose);
      }}
    >
      {formikProps => (
        <Modal
          title={
            config
              ? t("Edit custom field config", {
                  ns: "CustomFields",
                })
              : t("Add new custom field config", {
                  ns: "CustomFields",
                })
          }
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Close", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Save", {
                ns: "Global",
              }),
              buttonProps: {
                loading: formikProps.isSubmitting,
                form: formId,
                type: "submit",
                disabled: !formikProps.dirty || !formikProps.isValid,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack direction="column" spacing={1}>
              <SelectField
                name="objectType"
                label={t("Component", { ns: "CustomFields" })}
                options={customFieldObjectTypes.map(value => ({
                  label: customFieldObjectTypeLabels[value],
                  value,
                }))}
                required
              />
              <TextField
                name="key"
                label={t("Field key", { ns: "CustomFields" })}
                required
              />
              <TextField
                name="label"
                label={t("Field label", { ns: "CustomFields" })}
                required
              />
              <SelectField
                name="dataType"
                label={t("Field type", { ns: "CustomFields" })}
                options={customFieldDataTypes.map(value => ({
                  label: customFieldDataTypeLabels[value],
                  value,
                }))}
                required
              />
              {formikProps.values.dataType === "t_text" && (
                <OptionsField
                  name="allowedValues"
                  inputLabel={t("Picklist options", { ns: "CustomFields" })}
                  helperText={t("Press Enter to add new option", {
                    ns: "CustomFields",
                  })}
                />
              )}
              <TextField
                name="group"
                label={t("Category", { ns: "CustomFields" })}
              />
              <CheckboxField
                name="showAsColumnInList"
                label={t("Show as column", { ns: "CustomFields" })}
              />
              <CheckboxField
                name="showInCreateForm"
                label={t("Show in create form", { ns: "CustomFields" })}
              />
              <CheckboxField
                name="showValueAsChip"
                label={t("Show value as chip", { ns: "CustomFields" })}
              />
              {/* <CheckboxField
                name={`customFieldConfigs.${index}.isUnique`}
                label={"is unique"}
              /> */}
              {/* <CheckboxField
                name={`customFieldConfigs.${index}.mandatory`}
                label={"mandatory"}
              /> */}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
